import { useTranslation } from "react-i18next";
import { ArrowLeft, MapPinned, MousePointerClick, Ruler } from "lucide-react";
import clsx from "clsx";
import { Button } from "components/common/Button";
import { ConfirmActionModal } from "../ConfirmActionModal";
import { RoutesListViewV3 } from "./RoutesListViewV3";
import { RouteGroupSelectionStepV3 } from "./RouteGroupSelectionStepV3";
import type { RoutesCreationModeV3Props } from "./types";

export const RoutesCreationModeV3 = ({
  creationModeState,
  onOpenCalibration,
  isCalibrating,
  onCancelCalibration,
}: RoutesCreationModeV3Props) => {
  const { t } = useTranslation();
  const {
    routes,
    activeIndex,
    creationStep,
    selectedRouteTypeName,
    setSelectedRouteTypeName,
    routeTypeOptions,
    handleStartDrawing,
    handleCancelDrawing,
    handleSaveAll,
    handleBack,
    finalizeActiveRoute,
    isDrawingActive,
    toggleDrawingActive,
    isSaving,
    isDiscardModalOpen,
    handleConfirmDiscard,
    handleCancelDiscard,
    isCancelAllModalOpen,
    handleConfirmCancelAll,
    handleCloseCancelAll,
    pageMetersPerPdfPoint,
  } = creationModeState;

  const isAddingElements = creationStep === "adding-elements";
  const isDrawingRoutes = isAddingElements || creationStep === "calibrating";
  const hasRoutes = routes.length > 0;
  const isPageUncalibrated = pageMetersPerPdfPoint <= 0;

  const activeRoute =
    activeIndex >= 0 && activeIndex < routes.length
      ? routes[activeIndex]
      : null;

  const canFinalizeActiveRoute =
    activeRoute !== null &&
    !activeRoute.isFinalized &&
    activeRoute.nodes.length >= 2;

  const handleScaleButtonClick = () => {
    if (isCalibrating) {
      onCancelCalibration();
      return;
    }
    if (isDrawingActive) return;
    onOpenCalibration();
  };

  const handleDrawingButtonClick = () => {
    if (isCalibrating) {
      onCancelCalibration();
    }
    toggleDrawingActive();
  };

  return (
    <div className="w-full h-full bg-white rounded-lg border border-gray-200 shadow-sm p-4 overflow-hidden flex flex-col gap-3">
      <div className="w-full">
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={handleBack}
            startAdornment={<ArrowLeft className="w-4 h-4" />}
            aria-label={t("common.back")}
          />
          <h2 className="text-sm font-semibold text-gray-900">
            {t("smartSurveyor.routesCreation.title")}
          </h2>
        </div>
        {isDrawingRoutes && (
          <div className="mt-3 w-full flex items-center gap-2">
            <Button
              variant={isDrawingActive ? "primary" : "outline"}
              size="sm"
              onClick={handleDrawingButtonClick}
              startAdornment={<MousePointerClick className="w-3.5 h-3.5" />}
            >
              {isDrawingActive
                ? t("smartSurveyor.routesCreation.drawingOn")
                : t("smartSurveyor.routesCreation.drawingOff")}
            </Button>
            {isAddingElements && canFinalizeActiveRoute && (
              <Button variant="outline" size="sm" onClick={finalizeActiveRoute}>
                {t("smartSurveyor.routesCreation.finishRoute")}
              </Button>
            )}
          </div>
        )}
      </div>
      {!isDrawingRoutes && (
        <RouteGroupSelectionStepV3
          selectedRouteTypeName={selectedRouteTypeName}
          onRouteTypeNameChange={setSelectedRouteTypeName}
          routeTypeOptions={routeTypeOptions}
          onStartDrawing={handleStartDrawing}
        />
      )}
      {isDrawingRoutes && !hasRoutes && (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-gray-400">
          <MapPinned className="w-10 h-10 text-blue-500" />
          <p className="text-sm text-gray-500 text-center px-3">
            {isDrawingActive
              ? t("smartSurveyor.routesCreation.drawingHint")
              : t("smartSurveyor.routesCreation.drawingPausedHint")}
          </p>
        </div>
      )}
      {isDrawingRoutes && hasRoutes && (
        <>
          {isPageUncalibrated && (
            <div className="flex items-center gap-2 p-2 rounded-md border border-amber-200 bg-amber-50 text-xs text-amber-900">
              <Ruler className="w-4 h-4 flex-shrink-0" />
              <div className="flex-1 text-left">
                {t("smartSurveyor.routesCreation.calibrationRequired")}
              </div>
              <Button
                size="sm"
                className={clsx({ "!text-blue-600": !isCalibrating })}
                variant={isCalibrating ? "primary" : "outline"}
                onClick={handleScaleButtonClick}
                startAdornment={<Ruler className="w-3.5 h-3.5" />}
              >
                {isCalibrating
                  ? t("smartSurveyor.routesCreation.pickingPoints")
                  : t("smartSurveyor.routesCreation.setScale")}
              </Button>
            </div>
          )}
          <RoutesListViewV3 creationModeState={creationModeState} />
          {isAddingElements && (
            <div className="flex gap-2">
              <Button className="flex-1" onClick={handleCancelDrawing}>
                {t("smartSurveyor.routesCreation.cancelAll")}
              </Button>
              <Button
                className="flex-1"
                variant="primary"
                onClick={handleSaveAll}
                disabled={isSaving || isPageUncalibrated}
                title={
                  isPageUncalibrated
                    ? t("smartSurveyor.routesCreation.calibrationRequired")
                    : undefined
                }
              >
                {isSaving
                  ? t("smartSurveyor.routesCreation.saving")
                  : t("smartSurveyor.routesCreation.saveAll")}
              </Button>
            </div>
          )}
        </>
      )}
      <ConfirmActionModal
        isOpen={isDiscardModalOpen}
        onClose={handleCancelDiscard}
        onConfirm={handleConfirmDiscard}
        action="delete"
        title={t("smartSurveyor.routesCreation.discardModal.title")}
        description={t("smartSurveyor.routesCreation.discardModal.description")}
      />
      <ConfirmActionModal
        isOpen={isCancelAllModalOpen}
        onClose={handleCloseCancelAll}
        onConfirm={handleConfirmCancelAll}
        action="delete"
        title={t("smartSurveyor.routesCreation.cancelAllModal.title")}
        description={t(
          "smartSurveyor.routesCreation.cancelAllModal.description",
        )}
      />
    </div>
  );
};
